import { useTranslate, type Identifier } from "ra-core";
import { useState } from "react";
import { QueryFallback } from "@/components/admin/query-fallback";
import { Checkbox } from "@/components/ui/checkbox";

import {
  ActionTile,
  ActionTileControls,
  ActionTileName,
} from "../palmtree/badges";
import { dutchTodayHeading } from "../palmtree/dates";
import { NextActionButtons } from "../palmtree/NextActionButtons";
import { PlanActionButton } from "../palmtree/PlanActionButton";
import { panelClass, sectionLabelClass } from "../palmtree/shell";
import { useVandaagRows, type ActionRow } from "../palmtree/useOpenActions";
import { TaskEdit } from "../tasks/TaskEdit";

export const VandaagPage = () => {
  const translate = useTranslate();
  const { overdue, today, queries } = useVandaagRows();

  return (
    <div className={`${panelClass} relative max-w-3xl p-4 pb-16 md:p-5`}>
      <div className="mb-6">
        <h2 className="font-heading text-xl font-bold tracking-tight">
          {translate("palmtree.vandaag.title")}
        </h2>
        <p className="text-[12.5px] text-muted-foreground">
          {dutchTodayHeading()}
        </p>
      </div>
      <QueryFallback queries={queries}>
        <Section title={translate("palmtree.vandaag.overdue")} rows={overdue} />
        <Section title={translate("palmtree.vandaag.today")} rows={today} />

        {overdue.length === 0 && today.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground">
            {translate("palmtree.vandaag.empty")}
          </p>
        ) : null}

        <div className="mt-6">
          <PlanActionButton
            label={translate("palmtree.actions.plan_plus")}
            variant="default"
          />
        </div>
      </QueryFallback>
    </div>
  );
};

const Section = ({ title, rows }: { title: string; rows: ActionRow[] }) => {
  const [editId, setEditId] = useState<Identifier | null>(null);
  if (rows.length === 0) return null;
  return (
    <section className="mb-6">
      <h3 className={`${sectionLabelClass} mb-3`}>{title}</h3>
      <ul className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <ActionCard
            key={row.task.id}
            row={row}
            onEdit={() => setEditId(row.task.id)}
          />
        ))}
      </ul>
      {editId != null ? (
        <TaskEdit taskId={editId} open close={() => setEditId(null)} />
      ) : null}
    </section>
  );
};

const ActionCard = ({
  row,
  onEdit,
}: {
  row: ActionRow;
  onEdit: () => void;
}) => {
  const { task, contact } = row;
  return (
    <ActionTile contact={contact} className="flex flex-wrap items-center gap-3">
      <Checkbox checked={false} disabled aria-hidden className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <ActionTileName contact={contact} />
        <p className="text-[12.5px] text-muted-foreground">{task.text}</p>
      </div>
      <ActionTileControls>
        <NextActionButtons task={task} onEdit={onEdit} />
      </ActionTileControls>
    </ActionTile>
  );
};
